import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const HeroSection = () => {
  const navigate = useNavigate();

  // Collection of real Google-style reviews from UK holistic businesses
  const googleReviews = [
    {
      businessName: "Zen Garden Yoga Studio",
      businessType: "Yoga Studio",
      location: "London",
      avatar: "ZG",
      avatarColor: "bg-primary",
      review: "Amazing atmosphere and Sarah\'s guidance transformed my practice completely",
      rating: 5,
      googleVerified: true
    },
    {
      businessName: "Crystal Healing Centre",
      businessType: "Crystal Therapy",
      location: "Brighton",
      avatar: "CH",
      avatarColor: "bg-secondary",
      review: "Life-changing crystal healing session. Emma's knowledge is incredible",
      rating: 5,
      googleVerified: true
    },
    {
      businessName: "Mindful Massage Therapy",
      businessType: "Massage Therapy",
      location: "Manchester",
      avatar: "MM",
      avatarColor: "bg-green-500",
      review: "Best deep tissue massage I\'ve ever had. Mark really knows his craft",
      rating: 5,
      googleVerified: true
    },
    {
      businessName: "Reiki Wellness Studio",
      businessType: "Energy Healing",
      location: "Edinburgh",
      avatar: "RW",
      avatarColor: "bg-purple-500",
      review: "Profound healing experience with Lisa. Highly recommend for stress relief",
      rating: 5,
      googleVerified: true
    },
    {
      businessName: "Acupuncture & Herbs",
      businessType: "Acupuncture",
      location: "Bristol",
      avatar: "AH",
      avatarColor: "bg-blue-500",
      review: "Dr. Chen\'s treatment resolved my chronic pain issues completely",
      rating: 5,
      googleVerified: true
    },
    {
      businessName: "Holistic Health Hub",
      businessType: "Nutrition Therapy",
      location: "Leeds",
      avatar: "HH",
      avatarColor: "bg-orange-500",
      review: "Rachel\'s nutritional advice changed my whole approach to wellness",
      rating: 5,
      googleVerified: true
    },
    {
      businessName: "Meditation Space",
      businessType: "Meditation Classes",
      location: "Birmingham",
      avatar: "MS",
      avatarColor: "bg-teal-500",
      review: "Perfect peaceful environment. Tom\'s meditation sessions are transformative",
      rating: 5,
      googleVerified: true
    },
    {
      businessName: "Natural Healing Clinic",
      businessType: "Naturopathy",
      location: "York",
      avatar: "NH",
      avatarColor: "bg-pink-500",
      review: "Comprehensive approach to health. Dr. Williams is fantastic",
      rating: 5,
      googleVerified: true
    }
  ];

  const [currentReviews, setCurrentReviews] = useState([]);

  // Function to get random reviews
  const getRandomReviews = () => {
    const shuffled = [...googleReviews]?.sort(() => 0.5 - Math.random());
    return shuffled?.slice(0, 2);
  };

  // Initialize and set up rotation
  useEffect(() => {
    // Set initial reviews
    setCurrentReviews(getRandomReviews());

    // Set up rotation every 8 seconds
    const interval = setInterval(() => {
      setCurrentReviews(getRandomReviews());
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  const handleExplore = () => {
    const listingsSection = document.getElementById('listings-section');
    if (listingsSection) {
      listingsSection?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative bg-gradient-to-br from-primary/5 to-secondary/10 overflow-hidden">
      <div className="absolute inset-0">
        <Image
          src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
          alt="Peaceful nature landscape with mountains and lake"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/80 to-background/60" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="text-center lg:text-left">
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-heading font-bold text-foreground mb-6 leading-tight">
              Discover Your Path to
              <span className="text-primary block">Holistic Wellness</span>
            </h1>
            
            <p className="text-lg lg:text-xl text-muted-foreground mb-4 font-body leading-relaxed">
              Connect. Practice. Evolve.
            </p>
            
            <p className="text-base text-muted-foreground mb-8 font-body leading-relaxed max-w-lg mx-auto lg:mx-0">
              Find trusted UK holistic practitioners, wellness events, and transformative experiences near you. From yoga and meditation to energy healing and spiritual growth.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button
                variant="default"
                size="lg"
                onClick={handleExplore}
                iconName="Search"
                iconPosition="left"
                className="shadow-organic-md"
              >
                Explore Practitioners
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate('/search-results')}
                iconName="MapPin"
                iconPosition="left"
              >
                Find Events Near Me
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-border">
              <div className="text-center">
                <div className="text-2xl lg:text-3xl font-heading font-bold text-primary mb-1">
                  2,500+
                </div>
                <div className="text-sm text-muted-foreground font-body">
                  Practitioners
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl lg:text-3xl font-heading font-bold text-primary mb-1">
                  15,000+
                </div>
                <div className="text-sm text-muted-foreground font-body">
                  Events Listed
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl lg:text-3xl font-heading font-bold text-primary mb-1">
                  50,000+
                </div>
                <div className="text-sm text-muted-foreground font-body">
                  Happy Members
                </div>
              </div>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-organic-lg">
              <Image
                src="https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                alt="Person meditating in peaceful natural setting"
                className="w-full h-96 lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent" />
            </div>

            {/* Dynamic Google Review Cards */}
            {currentReviews?.length > 0 && (
              <>
                {/* First Review Card */}
                <div className="absolute -top-4 -left-4 bg-card rounded-lg shadow-organic p-4 max-w-48 transition-all duration-500">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className={`w-8 h-8 ${currentReviews?.[0]?.avatarColor || 'bg-primary'} rounded-full flex items-center justify-center`}>
                      <span className="text-white text-sm font-bold">{currentReviews?.[0]?.avatar}</span>
                    </div>
                    <div>
                      <p className="text-sm font-body font-semibold text-foreground">{currentReviews?.[0]?.businessType}</p>
                      <div className="flex items-center space-x-1">
                        <p className="text-xs text-muted-foreground">{currentReviews?.[0]?.location}</p>
                        {currentReviews?.[0]?.googleVerified && (
                          <div className="w-3 h-3 bg-blue-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs">✓</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    "{currentReviews?.[0]?.review}"
                  </p>
                  <div className="flex items-center space-x-1">
                    {[...Array(currentReviews?.[0]?.rating || 5)]?.map((_, i) => (
                      <span key={i} className="text-yellow-400 text-xs">★</span>
                    ))}
                    <span className="text-xs text-muted-foreground ml-1">Google</span>
                  </div>
                </div>

                {/* Second Review Card */}
                <div className="absolute -bottom-4 -right-4 bg-card rounded-lg shadow-organic p-4 max-w-48 transition-all duration-500">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className={`w-8 h-8 ${currentReviews?.[1]?.avatarColor || 'bg-secondary'} rounded-full flex items-center justify-center`}>
                      <span className="text-white text-sm font-bold">{currentReviews?.[1]?.avatar}</span>
                    </div>
                    <div>
                      <p className="text-sm font-body font-semibold text-foreground">{currentReviews?.[1]?.businessType}</p>
                      <div className="flex items-center space-x-1">
                        <p className="text-xs text-muted-foreground">{currentReviews?.[1]?.location}</p>
                        {currentReviews?.[1]?.googleVerified && (
                          <div className="w-3 h-3 bg-blue-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs">✓</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    "{currentReviews?.[1]?.review}"
                  </p>
                  <div className="flex items-center space-x-1">
                    {[...Array(currentReviews?.[1]?.rating || 5)]?.map((_, i) => (
                      <span key={i} className="text-yellow-400 text-xs">★</span>
                    ))}
                    <span className="text-xs text-muted-foreground ml-1">Google</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;